import { useTranslations } from '@vocab/react';
import { Stack, Strong, Text } from 'braid-design-system';
import { observer } from 'mobx-react';

import type { IFilterQuestion } from 'src/stores/JobApplicationStore/FiltersStore/FiltersStore';

import { getQuestionnaireAnswerFacetCount } from '../utils/getQuestionnaireAnswerFacetCount';

import translations from './.vocab/';
import { CheckboxRow } from './CheckboxRow';

interface Props {
  question: IFilterQuestion;
  onSelect: () => void;
  questionnaireAnswerFacets: Record<
    string,
    { id: string; count: number }
  > | null;
}

export const CheckboxFilterAnswers = observer(
  ({ question, onSelect, questionnaireAnswerFacets }: Props) => {
    const { t } = useTranslations(translations);

    return (
      <Stack space="medium">
        {question.showPreferredAnswersMessage && (
          <Text tone="secondary" size="xsmall">
            {t('Your matching answers are preselected', {
              Strong: (children) => (
                <Strong key={`${children}`}>{children}</Strong>
              ),
            })}
          </Text>
        )}

        <Stack space="large">
          {question.answersToDisplay.map((answer) => {
            const facetCount = getQuestionnaireAnswerFacetCount({
              answerId: answer.answerInfo.globalIdOrId,
              questionnaireAnswerFacets,
            });

            return (
              <CheckboxRow
                isPreferred={answer.answerInfo.isPreferred}
                key={answer.answerInfo.globalIdOrId}
                count={facetCount}
                text={answer.answerInfo.text}
                id={answer.answerInfo.globalIdOrId}
                isSelected={answer.isSelected}
                disabled={
                  // When loading or there's errors whilst getting facet i.e. facetCount===null,
                  // we still allow user to click the button
                  facetCount !== null
                    ? answer.isDisabled({ count: facetCount })
                    : false
                }
                onSelect={() => {
                  answer.onToggleAnswerSelected();
                  onSelect();
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  },
);
